// Origines CORS globales — /admin/origins*
import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, ShieldAlert, CheckCircle2, Plus, Pencil } from 'lucide-react';
import { EmptyState } from '../ui/UIComponents';
import {
  getOrigins,
  createOrigin,
  patchOrigin,
  deactivateOrigin,
  repriseOrigin,
} from '../../services/adminOrigins';
import { authService } from '../../services/authService';

function canModify() {
  // UI hint only — Worker enforces ORIGINES_MODIFIER / Super Admin
  const perms = authService.getPermissions();
  const codes = perms.map((p) => (typeof p === 'string' ? p : p?.code || p?.permission || '')).filter(Boolean);
  const user = authService.getCurrentUser();
  const n = user?.niveau;
  if (n === 1 || n === '1' || (typeof n === 'string' && n.toUpperCase() === 'SUPER_ADMIN')) return true;
  return codes.includes('ORIGINES_MODIFIER');
}

function statusLabel(status, http) {
  if (status === 'termine' || http === 200) return { text: 'Propagation réussie', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' };
  if (status === 'partiel' || http === 207) return { text: 'Propagation partielle', cls: 'text-amber-800 bg-amber-50 border-amber-200' };
  if (status === 'echec' || http === 502) return { text: 'Propagation échouée', cls: 'text-rose-700 bg-rose-50 border-rose-200' };
  if (status === 'aucune_cible_configuree' || http === 503) {
    return { text: 'Aucun service distant configuré', cls: 'text-slate-700 bg-slate-100 border-slate-200' };
  }
  return { text: status || 'Résultat inconnu', cls: 'text-slate-600 bg-slate-50 border-slate-200' };
}

function extractOriginList(res) {
  if (!res) return [];
  if (Array.isArray(res.origins)) return res.origins;
  if (Array.isArray(res.origines)) return res.origines;
  if (Array.isArray(res.items)) return res.items;
  if (Array.isArray(res.data)) return res.data;
  // Agrégation possible : inventaire local Admin
  if (Array.isArray(res.inventaire)) return res.inventaire;
  return [];
}

function PropagationResult({ res }) {
  if (!res) return null;
  const http = res._httpStatus;
  const st = statusLabel(res.status, http);
  return (
    <div className={`p-3 rounded-2xl border text-xs space-y-2 ${st.cls}`}>
      <p className="font-bold">{st.text}</p>
      {res.operation ? <p className="text-slate-600">Opération : {res.operation}</p> : null}
      {Array.isArray(res.succes) && res.succes.length > 0 && (
        <p>Services mis à jour : {res.succes.join(', ')}</p>
      )}
      {Array.isArray(res.echecs) && res.echecs.length > 0 && (
        <p>Services en échec : {res.echecs.join(', ')}</p>
      )}
      {Array.isArray(res.skipped) && res.skipped.length > 0 && (
        <p>Services non contactés : {res.skipped.join(', ')}</p>
      )}
      {res.results && typeof res.results === 'object' && (
        <details>
          <summary className="cursor-pointer font-semibold">Détail par service</summary>
          <ul className="mt-1 space-y-1 max-h-40 overflow-y-auto">
            {Object.entries(res.results).map(([k, v]) => (
              <li key={k} className="text-[11px]">
                <span className="font-semibold">{k}</span>
                {typeof v === 'object' && v !== null && v.error
                  ? ` — ${v.error}`
                  : typeof v === 'object' && v !== null && v.status
                    ? ` — ${v.status}`
                    : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

export default function OriginsTab() {
  const [list, setList] = useState([]);
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastOp, setLastOp] = useState(null);
  const [busy, setBusy] = useState(false);
  const mod = canModify();

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ origin: '', label: '' });
  const [edit, setEdit] = useState(null); // { id, label, status }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getOrigins();
      setRaw(res);
      setList(extractOriginList(res));
      if (res?.status) setLastOp(res);
    } catch (err) {
      setList([]);
      setError(
        err?.status === 403
          ? 'Vous n’avez pas l’autorisation de consulter les origines autorisées.'
          : err?.message || 'Impossible de charger les origines.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runCreate = async () => {
    if (!form.origin.trim()) {
      setError('L’adresse (URL) est obligatoire.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload = { origin: form.origin.trim() };
      if (form.label.trim()) payload.label = form.label.trim();
      const res = await createOrigin(payload);
      setLastOp(res);
      setShowCreate(false);
      setForm({ origin: '', label: '' });
      await load();
    } catch (err) {
      setError(err?.message || 'Création impossible.');
      if (err?.body) setLastOp({ ...err.body, _httpStatus: err.status });
    } finally {
      setBusy(false);
    }
  };

  const runPatch = async () => {
    if (!edit?.id) return;
    setBusy(true);
    setError(null);
    try {
      const payload = {};
      if (edit.label !== undefined) payload.label = edit.label;
      if (edit.status !== undefined && edit.status !== '') payload.status = edit.status;
      const res = await patchOrigin(edit.id, payload);
      setLastOp(res);
      setEdit(null);
      await load();
    } catch (err) {
      setError(err?.message || 'Modification impossible.');
      if (err?.body) setLastOp({ ...err.body, _httpStatus: err.status });
    } finally {
      setBusy(false);
    }
  };

  const runDeactivate = async (id) => {
    if (!window.confirm('Désactiver cette origine sur les services connectés ?')) return;
    setBusy(true);
    setError(null);
    try {
      const res = await deactivateOrigin(id);
      setLastOp(res);
      await load();
    } catch (err) {
      setError(err?.message || 'Désactivation impossible.');
      if (err?.body) setLastOp({ ...err.body, _httpStatus: err.status });
    } finally {
      setBusy(false);
    }
  };

  const runReprise = async (id) => {
    if (!window.confirm('Relancer l’application de cette origine sur les services qui ont échoué ou n’ont pas été contactés ?')) return;
    setBusy(true);
    setError(null);
    try {
      const res = await repriseOrigin(id);
      setLastOp(res);
      await load();
    } catch (err) {
      setError(err?.message || 'Reprise impossible.');
      if (err?.body) setLastOp({ ...err.body, _httpStatus: err.status });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex gap-2">
          <ShieldAlert size={14} /> {error}
        </div>
      )}
      <PropagationResult res={lastOp} />

      <div className="flex flex-wrap gap-2 justify-between">
        <p className="text-xs text-slate-500">
          Origines autorisées pour l’accès à la plateforme. Les changements sont appliqués aux services connectés.
        </p>
        <div className="flex gap-2">
          <button type="button" className="dsi-btn-secondary text-xs" onClick={load} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Actualiser
          </button>
          {mod && (
            <button type="button" className="dsi-btn-primary text-xs" onClick={() => setShowCreate(true)}>
              <Plus size={14} /> Ajouter une origine
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="dsi-card p-10 text-center text-xs text-slate-500">Chargement…</div>
      ) : (
        <div className="dsi-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 font-semibold border-b">
                <tr>
                  <th className="px-3 py-2">ID</th>
                  <th className="px-3 py-2">Adresse</th>
                  <th className="px-3 py-2">Libellé</th>
                  <th className="px-3 py-2">Statut</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.map((o) => {
                  const id = o.id || o.origin_id;
                  return (
                    <tr key={id || o.origin} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-mono text-[10px] max-w-[140px] truncate">{id || '—'}</td>
                      <td className="px-3 py-2 text-xs">{o.origin || o.url || '—'}</td>
                      <td className="px-3 py-2 text-xs">{o.label || '—'}</td>
                      <td className="px-3 py-2 text-xs">{o.status || '—'}</td>
                      <td className="px-3 py-2 text-right">
                        {mod && id && (
                          <div className="inline-flex flex-wrap gap-1 justify-end">
                            <button
                              type="button"
                              className="dsi-btn-ghost text-[10px]"
                              onClick={() => setEdit({ id, label: o.label || '', status: o.status || '' })}
                            >
                              <Pencil size={12} /> Modifier
                            </button>
                            <button type="button" className="dsi-btn-ghost text-[10px]" onClick={() => runDeactivate(id)}>
                              Désactiver
                            </button>
                            <button type="button" className="dsi-btn-ghost text-[10px]" onClick={() => runReprise(id)}>
                              Reprise
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!list.length && (
            <EmptyState
              title="Aucune origine"
              message={
                raw
                  ? 'Aucune origine à afficher pour le moment. Consultez le résultat de la dernière opération si besoin.'
                  : 'Aucune origine enregistrée.'
              }
            />
          )}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => !busy && setShowCreate(false)} />
          <div className="relative w-full max-w-md dsi-card p-6 space-y-3">
            <h3 className="font-bold">Ajouter une origine</h3>
            <label className="block text-xs space-y-1">
              <span className="font-semibold">Adresse (URL)</span>
              <input
                className="dsi-input"
                value={form.origin}
                onChange={(e) => setForm({ ...form, origin: e.target.value })}
                placeholder="https://app.example.com"
              />
            </label>
            <label className="block text-xs space-y-1">
              <span className="font-semibold">Libellé (optionnel)</span>
              <input
                className="dsi-input"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
              />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="dsi-btn-secondary text-xs" disabled={busy} onClick={() => setShowCreate(false)}>
                Annuler
              </button>
              <button type="button" className="dsi-btn-primary text-xs" disabled={busy} onClick={runCreate}>
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => !busy && setEdit(null)} />
          <div className="relative w-full max-w-md dsi-card p-6 space-y-3">
            <h3 className="font-bold">Modifier l’origine</h3>
            <p className="text-[11px] text-slate-500">Identifiant : {edit.id}</p>
            <p className="text-[11px] text-slate-500">Vous pouvez modifier le libellé et le statut.</p>
            <label className="block text-xs space-y-1">
              <span className="font-semibold">Label</span>
              <input
                className="dsi-input"
                value={edit.label}
                onChange={(e) => setEdit({ ...edit, label: e.target.value })}
              />
            </label>
            <label className="block text-xs space-y-1">
              <span className="font-semibold">Status</span>
              <input
                className="dsi-input"
                value={edit.status}
                onChange={(e) => setEdit({ ...edit, status: e.target.value })}
                placeholder="Statut"
              />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="dsi-btn-secondary text-xs" disabled={busy} onClick={() => setEdit(null)}>
                Annuler
              </button>
              <button type="button" className="dsi-btn-primary text-xs" disabled={busy} onClick={runPatch}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
